/**
 * Adobe Launch Tracking
 *
 * Implements comprehensive event tracking across all widgets and page sections
 * using the MOVEAnalytics SDK.
 *
 * USAGE:
 * - Add data-tracking="event:name" attribute to any element (e.g., data-tracking="local:landing:card_summary:for_sale_srp")
 * - Single delegated event handler processes all [data-tracking] attributes
 * - useUtm array determines which events get ?iid=local_{geo} parameters
 * - Special handlers for card clicks (due to card_viewer.js behavior)
 * - Special handlers for share/footer social icons (use class selectors)
 *
 * @see /public/wordpress/wp-content/mu-plugins/mmg-realtor0265/docs/EVENT_TRACKING.md
 */

(function($) {
  'use strict';

  // Ensure MOVEAnalytics is available
  if (typeof window.MOVEAnalytics === 'undefined') {
    console.warn('MOVEAnalytics SDK not loaded. Tracking events will not fire.');
    return;
  }

  const pageType = window?.Mmg?.pageType;
  const isSchoolPage = pageType?.startsWith('school');

  /**
   * Array of event names that require UTM parameters
   * Events in this array will have ?iid=local_{geo} added to their href
   */
  const useUtm = [
    'local:landing:card_summary:for_sale_srp',
    'local:landing:card_summary:for_rent_srp',
    'local:landing:new_construct_ad:cta:start_now',
    'local:landing:crm_signup:cta:sign_up',
    'local:landing:card_explore:for_sale_srp',
    'local:landing:card_explore:for_rent_srp',
    'local:landing:card_schools:school_link',
    'local:landing:sellers:cta:find_agent',
    'local:landing:sellers:cta:sell_my_home',
    'local:landing:sellers:cta:claim_home',
    'local:landing:renters:find_rental:for_rent_srp',
    'local:landing:card_home_values:highlighted_property_ldp',
    'local:landing:renters:highlighted_property_ldp',
    'local:landing:buyers:thumbnail:new_listings',
    'local:landing:buyers:thumbnail:recently_sold',
    'local:landing:buyers:thumbnail:open_houses',
    'local:landing:buyers:thumbnail:new_construction',
    'local:landing:buyers:thumbnail:condos',
  ];


  /**
   * Add UTM parameter to a URL
   * @param {string} url - The URL to modify
   * @returns {string} - URL with ?iid=local_{geo} parameter added
   */
  function addUTMParameter(url) {
    const utm = window?.Mmg?.realtor?.utm;
    console.log('[Tracking] UTM:', utm);
    if(!utm) {
      return url;
    }
    
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}iid=${utm}`;
  }

  /**
   * Track a click event and optionally add UTM parameters to the link
   * @param {string} linkname - The tracking linkname
   * @param {Element} element - The DOM element (optional)
   */
  function trackClick(linkname, element) {
    const addUTM = useUtm.includes(linkname);
    const properties = {
      linkName: linkname,
      pageId: 'home',
      ...structuredClone(window?.Mmg?.realtor?.properties),
    };

    // School pages send no label
    if (!isSchoolPage) {
      properties.label = linkname;
    }

    console.log('[Tracking] Click:', linkname, JSON.stringify(properties), addUTM && `(with UTM)`);
    MOVEAnalytics.trackEvent('click', properties);

    // Add UTM parameter if event requires it and element has href
    if (addUTM && element && element.href) {
      element.href = addUTMParameter(element.href);
    }
  }

  /**
   * Initialize dynamic data-tracking attribute handler
   * Handles all elements with [data-tracking] attribute
   * The attribute value should be the event name (e.g., "local:landing:card_summary:for_sale_srp")
   */
  function initDataTrackingHandler() {
    $(document).on('click', '[data-tracking]', function(e) {
      const eventName = $(this).attr('data-tracking');
      trackClick(eventName, this);
    });
  }

  /**
   * Initialize Share Events tracking
   * Tracks share widget clicks (no modal - direct share links), with the
   * school page labels on school pages and the GLP labels everywhere else
   */
  function initShareTracking() {
    // No share spec for the school state and national pages
    if (pageType === 'school_state' || pageType === 'school_country') {
      return;
    }

    const labels = isSchoolPage
      ? {
        copyLink: 'local:share:copy_link',
        x: 'local:share:twitter',
        facebook: 'local:share:facebook',
        linkedin: 'local:share:linkedin',
      }
      : {
        copyLink: 'local:landing:primary:share',
        x: 'local:landing:x:share',
        facebook: 'local:landing:facebook:share',
        linkedin: 'local:landing:linkedin:share',
      };

    // Track copy link button click
    $(document).on('click', '.sc-share__copy-link', function(e) {
      trackClick(labels.copyLink, this);
    });

    // Track X/Twitter share button click
    $(document).on('click', '.sc-share__social-icon.icon-x', function(e) {
      trackClick(labels.x, this);
    });

    // Track Facebook share button click
    $(document).on('click', '.sc-share__social-icon.icon-facebook', function(e) {
      trackClick(labels.facebook, this);
    });

    // Track LinkedIn share button click
    $(document).on('click', '.sc-share__social-icon.icon-linkedin', function(e) {
      trackClick(labels.linkedin, this);
    });
  }

  /**
   * Initialize Explore Urban Areas tracking
   * Tracks search button CTAs for housing and rental markets
   */
  function initUrbanAreasTracking() {
    // Track "Search {} Housing Market" CTA
    $(document).on('click', '.sc-content-block--search-buttons a[href*="realestateandhomes-search"]', function(e) {
      trackClick('local:landing:card_explore:for_sale_srp', this);
    });

    // Track "Search {} Rental Market" CTA
    $(document).on('click', '.sc-content-block--search-buttons a[href*="apartments"]', function(e) {
      trackClick('local:landing:card_explore:for_rent_srp', this);
    });
  }

  /**
   * Initialize Card Viewer tracking
   * Special handling for card clicks due to card_viewer.js behavior
   * card_viewer.js intercepts clicks and programmatically triggers .card__link[0].click()
   */
  function initCardViewerTracking() {
    // Track urban area cards (GLP tabs)
    $(document).one('click', '.sc-card-viewer--urban-areas .card', function(e) {
      const $card = $(this);
      const link = $card.find('.card__link')[0];
      trackClick('local:landing:card_explore:image_geo_local', link);
    });

    // Track property highlight LDP links (for sale)
    $(document).one('click', '.sc-card-viewer--properties-for-sale .card', function(e) {
      const $card = $(this);
      const link = $card.find('.card__link')[0];
      trackClick('local:landing:card_home_values:highlighted_property_ldp', link);
    });

    // Track property highlight LDP links (for rent)
    $(document).one('click', '.sc-card-viewer--properties-for-rent .card', function(e) {
      const $card = $(this);
      const link = $card.find('.card__link')[0];
      trackClick('local:landing:renters:highlighted_property_ldp', link);
    });
  }

  /**
   * Initialize Links tracking
   * The core widget renders the links, so the list's label comes from its
   * data-json, set by LinksFilters
   */
  function initLinksTracking() {
    $(document).on('click', '.sc-links__nav a', function(e) {
      const label = $(this).closest('.sc-links').data('json')?.tracking;

      if (label) {
        trackClick(label, this);
      }
    });
  }

  /**
   * Initialize Breadcrumbs tracking
   * The core widget renders the links, so they are matched by selector
   */
  function initBreadcrumbTracking() {
    $(document).on('click', '.sc-breadcrumbs a', function(e) {
      trackClick('local:breadcrumb:link', this);
    });
  }

  /**
   * Initialize Search tracking
   * A search either submits the form or follows a suggestion link
   */
  function initSearchTracking() {
    $(document).on('submit', '.sc-search form', function(e) {
      trackClick('local:header_search:input', this);
    });

    $(document).on('click', '.sc-search .tt-menu a', function(e) {
      trackClick('local:header_search:input', this);
    });
  }

  /**
   * Initialize Explore map tracking
   * The map opens in the shared global modal, so its close button is only
   * tracked while the modal holds the map canvas
   */
  function initMapTracking() {
    $(document).on('click', '.modal-global .close', function(e) {
      if ($(this).closest('.modal-global').find('.sc-map__canvas').length) {
        trackClick('local:map:explore:close', this);
      }
    });
  }

  /**
   * Initialize Market Clock widget tracking
   * - Widget impression via IntersectionObserver on the iframe element
   * - CTA click detected via window blur when activeElement is the iframe
   */
  function initMarketClockTracking() {
    const iframe = $('.sc-market-clock iframe')[0];
    if (!iframe) return;

    const observer = new IntersectionObserver(function(entries) {
      if (!entries[0].isIntersecting) return;
      console.log('[Tracking] Widget Impression: market_clock');
      MOVEAnalytics.trackEvent('widgetImpression', {
        widgetName: 'market_clock',
        linkName: 'local:landing:market_clock:widget_impression',
        label: 'local:landing:market_clock:widget_impression',
        pageId: 'home',
        ...structuredClone(window?.Mmg?.realtor?.properties),
      });
      observer.disconnect();
    }, { threshold: 0.5 });
    observer.observe(iframe);

    // Clicks inside an iframe don't bubble to the parent DOM. The iframe
    // explicitly notifies the parent via postMessage on click.
    $(window).on('message', function(event) {
      if (event.originalEvent.data !== 'market_clock_click') return;
      console.log('[Tracking] Click: local:landing:market_clock:cta:check_the_clock');
      MOVEAnalytics.trackEvent('click', {
        widgetName: 'market_clock',
        linkName: 'local:landing:market_clock:cta:check_the_clock',
        label: 'local:landing:market_clock:cta:check_the_clock',
        pageId: 'home',
        ...structuredClone(window?.Mmg?.realtor?.properties),
      });
    });
  }

  function trackPageLoad() {
    const properties = structuredClone(window?.Mmg?.realtor?.properties ?? {});
    const pageName = window?.Mmg?.realtor?.pageName;

    if (!pageName) {
      return;
    }

    console.log('[Tracking] Page Load:', pageName, properties);
    MOVEAnalytics.trackPage(pageName, properties);
  }

  /**
   * Initialize all tracking modules
   */
  function init() {
    trackPageLoad();

    // Wait for DOM to be ready
    $(document).ready(function() {
      initDataTrackingHandler();
      initShareTracking();
      initUrbanAreasTracking();
      initCardViewerTracking();
      initMarketClockTracking();
      initLinksTracking();

      if (pageType === 'school' || pageType === 'school_district') {
        initMapTracking();
        initBreadcrumbTracking();
      }

      if (isSchoolPage) {
        initSearchTracking();
      }

      console.log('Adobe Launch Tracking initialized');
    });
  }

  // Initialize tracking
  init();

})(jQuery);
